/**
 * Effective deadline of a task: the earliest of its own due date, the end of
 * the list it is in and the end of that project.
 *
 * Only computed, never stored as the task's dueDate: the task keeps its own
 * date (and the backend no longer copies list dates onto tasks). What is
 * stored is the urgency derived from it, as before - for every task in the
 * store, so all views agree.
 */
import { TaskDto } from "../features/tasks/types";
import { urgencyFormula } from "./urgencyFormula";

type DateLike = string | Date | null | undefined;

interface ListSource {
  id: string;
  name?: string;
  dueDate?: DateLike;
  notes?: { noteId: string }[];
}

interface ProjectSource {
  id: string;
  name?: string;
  dueDate?: DateLike;
  completedAt?: DateLike;
  cancelledAt?: DateLike;
  Collections?: ListSource[];
  inboxCollection?: ListSource | null;
}

export interface InheritedDeadline {
  date: number;
  source: "list" | "project";
  /** Name of the list or project the date comes from */
  name?: string;
  /** The list is the project's inbox (which has no name of its own) */
  isInbox?: boolean;
}

export interface EffectiveDueDate {
  date?: number;
  source: "task" | "list" | "project" | "none";
  name?: string;
  isInbox?: boolean;
}

const toMs = (date?: DateLike) => (date ? new Date(date).getTime() : undefined);

/**
 * noteId → the earliest deadline its lists and projects impose. The loaded
 * project (with full lists) wins over its entry in the project list.
 */
export function getInheritedDeadlines(
  projects: ProjectSource[] = [],
  project?: ProjectSource | null,
): Map<string, InheritedDeadline> {
  const result = new Map<string, InheritedDeadline>();
  const all = [
    ...(project ? [project] : []),
    ...projects.filter((p) => p.id !== project?.id),
  ];

  for (const proj of all) {
    // A closed project no longer pushes its tasks
    if (proj.completedAt || proj.cancelledAt) continue;
    const projectEnd = toMs(proj.dueDate);
    const lists = [
      ...(proj.Collections ?? []),
      ...(proj.inboxCollection ? [proj.inboxCollection] : []),
    ];
    for (const list of lists) {
      const listEnd = toMs(list.dueDate);
      const candidate: InheritedDeadline | undefined =
        listEnd !== undefined &&
        (projectEnd === undefined || listEnd <= projectEnd)
          ? {
              date: listEnd,
              source: "list",
              name: list.name,
              isInbox: list.id === proj.inboxCollection?.id,
            }
          : projectEnd !== undefined
            ? { date: projectEnd, source: "project", name: proj.name }
            : undefined;
      if (!candidate) continue;
      for (const { noteId } of list.notes ?? []) {
        const known = result.get(noteId);
        if (!known || candidate.date < known.date)
          result.set(noteId, candidate);
      }
    }
  }
  return result;
}

export function getEffectiveDueDate(
  task: Pick<TaskDto, "id" | "dueDate">,
  inherited: Map<string, InheritedDeadline>,
): EffectiveDueDate {
  const own = toMs(task.dueDate);
  const from = inherited.get(task.id);
  if (from && (own === undefined || from.date < own))
    return {
      date: from.date,
      source: from.source,
      name: from.name,
      isInbox: from.isInbox,
    };
  if (own !== undefined) return { date: own, source: "task" };
  return { source: "none" };
}

/** Urgency (0-100) a deadline at this time implies */
export const urgencyForDate = (date: number) =>
  urgencyFormula((date - Date.now()) / (1000 * 60 * 60 * 24) + 1);

/**
 * The least urgency a task's list or project deadline requires. Only while
 * the task has no own date - an own date decides (and locks) by itself.
 */
export function getInheritedUrgency(
  task: Pick<TaskDto, "id" | "dueDate">,
  inherited: Map<string, InheritedDeadline>,
): number | undefined {
  if (task.dueDate) return undefined;
  const from = inherited.get(task.id);
  return from ? urgencyForDate(from.date) : undefined;
}

/**
 * Stored urgency:
 * - own due date: follows it (and the pad locks it)
 * - otherwise: the urgency set by hand, but at least what the list's or
 *   project's deadline requires - it can be raised by hand, not lowered
 *   below that. The hand-set value is kept in manualUrgency, so it comes
 *   back once the floor drops or goes away.
 */
export function calculateUrgencyForDueTasks<T extends TaskDto>(
  tasks: T[],
  inherited: Map<string, InheritedDeadline> = new Map(),
): T[] {
  return tasks.map((task) => {
    if (task.dueDate) {
      const urgency = urgencyForDate(new Date(task.dueDate).getTime());
      return { ...task, urgency, computedUrgency: urgency };
    }
    // Partial objects (no urgency at all) are left as they are
    if (typeof task.urgency !== "number") return task;
    // A changed urgency (fresh from the backend or set elsewhere) is the
    // new manual value; otherwise keep the one remembered
    const changed =
      task.computedUrgency === undefined ||
      task.urgency !== task.computedUrgency;
    const manual = changed
      ? task.urgency
      : (task.manualUrgency ?? task.urgency);
    const floor = getInheritedUrgency(task, inherited);
    const urgency = floor === undefined ? manual : Math.max(manual, floor);
    return {
      ...task,
      urgency,
      manualUrgency: manual,
      computedUrgency: urgency,
    };
  });
}

/** Store state this needs - kept loose to avoid importing the slice */
interface UrgencyState {
  tasks: TaskDto[];
  task?: TaskDto | null;
  projects?: ProjectSource[];
  project?: ProjectSource | null;
}

/**
 * Recomputes the stored urgency of all tasks after list or project dates or
 * memberships changed (the floor from their deadlines moved).
 */
export function refreshUrgency(state: UrgencyState) {
  const inherited = getInheritedDeadlines(state.projects, state.project);
  state.tasks = calculateUrgencyForDueTasks(state.tasks, inherited);
  if (state.task)
    state.task = calculateUrgencyForDueTasks([state.task], inherited)[0];
}
